import './style.scss';

import Walkthrough from '../_base';

class WalkthroughCreateRecipe extends Walkthrough {
	/**
	 * Translatable strings for the walkthrough.
	 * 
	 * @return {Object} The translatable strings for the walkthrough
	 */
	get strings() {
		return UncannyAutomator._core.i18n.walkthrough.create_recipe;
	};

	/**
	 * The ID of the walkthrough.
	 * 
	 * @return {String} The ID of the walkthrough
	 */
	get walkthroughID() {
		return 'create-recipe';
	};

	/**
	 * Events that start the tour
	 * 
	 * @return {Array} The events that start the tour
	 */
	get tourStartEvents() {
		return [ 'uap/builder/legacy/show-recipe-type-selector' ];
	};

	/**
	 * Steps for the walkthrough
	 * 
	 * @return {Object[]} The steps for the walkthrough
	 */
	get steps() {
		if ( _uo.utility.isEmpty( this.tour ) ) {
			return [];
		}

		return [
			/**
			 * Step 1: Welcome
			 *
			 * @description This step displays a welcome message to the user, introducing them to the Uncanny Automator tour.
			 * @next It contains a "Next" button that, when clicked, proceeds to the next step in the tour.
			 */
			{
				id: 'welcome',
				title: this.strings.welcome.title,
				// This step also has an image that's defined using CSS
				text: this.strings.welcome.message,
				buttons: [
					{
						text: this.strings.welcome.next_btn_label,
						action: this.tour.next
					}
				],
			},

			/**
			 * Step 2: Recipe type
			 *
			 * @description This step instructs the user to select a recipe type. It highlights the relevant element on the page.
			 * @next The next step is triggered automatically when the user selects the "Logged-in users" recipe type.
			 * @listens 'uap/builder/legacy/selected-recipe-type'
			 */
			{
				id: 'recipe-type',
				title: this.strings.recipe_type.title,
				text: `
					<p>${ this.strings.recipe_type.message }</p>
					<p><a href="https://automatorplugin.com/knowledge-base/creating-a-recipe/" target="_blank">${ this.strings.recipe_type.learn_more } <uo-icon id="external-link"></uo-icon></a></p>
				`,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '.uap-recipe-type-item[data-id="user"]',
					on: 'left'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour if the selected recipe type is 'user'.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/selected-recipe-type' event.
					 * When the event is triggered, it checks if the recipe type is 'user'. If it is, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/selected-recipe-type'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( event.detail.recipeType !== 'user' ) {
								return;
							};

							this.tour.next();
						};

						this.addEventListener(
							'recipe-type',
							'uap/builder/legacy/selected-recipe-type',
							handler
						);
					}
				}
			},

			/**
			 * Step 3: Confirm recipe type
			 *
			 * @description This step instructs the user to confirm the selected recipe type by clicking a confirmation button.
			 * @next The next step is triggered automatically when the user confirms the recipe type.
			 * @listens 'uap/builder/core/set-recipe-type'
			 */
			{
				id: 'recipe-type-confirm',
				title: this.strings.recipe_type_confirm.title,
				text: this.strings.recipe_type_confirm.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#uap-recipe-type-confirm',
					on: 'left'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour when the recipe type is confirmed.
					 *
					 * This method sets up an event listener for the 'uap/builder/core/set-recipe-type' event.
					 * When the event is triggered, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/core/set-recipe-type'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = () => this.tour.next();

						this.addEventListener(
							'recipe-type-confirm',
							'uap/builder/core/set-recipe-type',
							handler
						);
					}
				}
			},

			/**
			 * Step 4: Set title
			 *
			 * @description This step guides the user to set the recipe title by typing text into the title field.
			 * @next The "Next" button is disabled by default and is enabled only after the title is saved successfully.
			 * @listens 'uap/builder/core/set-recipe-title'
			 */
			{
				id: 'set-title',
				title: this.strings.set_title.title,
				text: this.strings.set_title.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#titlewrap #title',
					on: 'bottom'
				},
				buttons: [
					{
						text: this.strings.set_title.next_btn_label,
						action: this.tour.next,
						// Disabled by default until we save the title
						disabled: true
					}
				],
				when: {
					/**
					 * Proceeds to the next step of the tour when the "Next" button is clicked.
					 *
					 * This method hides the WordPress label, resets the value of the title field, and types the text into the title field.
					 * After the text has been typed, it sets the recipe title and checks if the call was successful.
					 * If the call was successful, it gets the current step and enables the "Next" button.
					 */
					show: () => {
						/**
						 * The label that WordPress adds above the input field.
						 * @type {HTMLLabelElement}
						 */
						const $wordPressTitleLabel = document.getElementById( 'title-prompt-text' );

						/**
						 * The input field where the text will be typed.
						 * @type {HTMLInputElement}
						 */
						const $titleField = document.getElementById( 'title' );

						/**
						 * The text to be typed into the input field.
						 * @type {String}
						 */
						const text = this.strings.set_title.set_title_content;

						// Hide the WordPress label
						$wordPressTitleLabel.style.display = 'none';

						// Reset the value of the field
						$titleField.value = '';

						// Type the text into the field
						setTimeout( () => {
							_uo.animation.typeWriter(
								// Field
								$titleField,
								// The text
								text,
								// Callback function to be executed when the text has been typed
								() => {
									// Save the title
									UncannyAutomator._core.recipe.setRecipeTitle( text )
										.then( response => {
											// Check if the call was successful
											if ( ! response.success ) {
												return;
											}

											// Get the step
											const step = this.tour.getById( 'set-title' );

											// Enable the next button
											step.updateStepOptions( {
												buttons: [
													{
														text: this.strings.set_title.next_btn_label,
														action: this.tour.next,
														disabled: false, // Enable the button
													}
												]
											} );
										} );
								}
							);
						}, 200 );
					}
				},
			},

			/**
			 * Step 5: Triggers - All integrations
			 *
			 * @description This step shows the user the available integrations for triggers.
			 * @next It contains a "Next" button that, when clicked, proceeds to the next step in the tour.
			 */
			{
				id: 'triggers-all-integrations',
				title: this.strings.triggers_all_integrations.title,
				text: this.strings.triggers_all_integrations.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#uap-trigger-all-integrations-link',
					on: 'right'
				},
				buttons: [
					{
						text: this.strings.triggers_all_integrations.next_btn_label,
						action: this.tour.next
					}
				]
			},

			/**
			 * Step 6: Trigger select integration
			 *
			 * @description This step guides the user to select the WordPress integration for the recipe's trigger.
			 * @next The next step is triggered automatically when the WordPress integration is selected.
			 * @listens 'uap/builder/legacy/integration-selected'
			 */
			{
				id: 'trigger-select-integration',
				title: this.strings.trigger_select.title,
				text: `<p>${ this.strings.trigger_select.message1 }</p><p>${ this.strings.trigger_select.message2 }</p>`,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-triggers-ui .item-integrations .item-integration[data-integration-id="WP"]',
					on: 'right'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour when the "Next" button is clicked.
					 *
					 * This method hides the WordPress label, resets the value of the title field, and types the text into the title field.
					 * After the text has been typed, it sets the recipe title and checks if the call was successful.
					 * If the call was successful, it gets the current step and enables the "Next" button.
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'trigger' ||
								event.detail.integrationCode !== 'WP'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'trigger-select-integration',
							'uap/builder/legacy/integration-selected',
							handler
						);
					}
				}
			},

			/**
			 * Step 7: Trigger select item
			 *
			 * @description This step guides the user to select the "A user publishes a post" trigger for the recipe.
			 * @next The next step is triggered automatically when the trigger is selected.
			 * @listens 'uap/builder/legacy/item-created'
			 */
			{
				id: 'trigger-select-item',
				title: this.strings.trigger_select_item.title,
				text: this.strings.trigger_select_item.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-triggers-ui .item-task-selector__option[data-integration-id="WP"][data-item-id="WP_USER_POST_PUBLISHED"]',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 0,
				when: {
					/**
					 * Proceeds to the next step of the tour when the "Next" button is clicked.
					 *
					 * This method hides the WordPress label, resets the value of the title field, and types the text into the title field.
					 * After the text has been typed, it sets the recipe title and checks if the call was successful.
					 * If the call was successful, it gets the current step and enables the "Next" button.
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'trigger' ||
								event.detail.integrationCode !== 'WP' ||
								event.detail.itemCode !== 'WP_USER_POST_PUBLISHED'
							) {
								return;
							}

							// Save the trigger ID
							this._triggerID = event.detail.itemID;

							this.tour.next();
						};

						this.addEventListener(
							'trigger-select-item',
							'uap/builder/legacy/item-created',
							handler
						);
					}
				}
			},

			/**
			 * Step 8: Trigger open "Post type" dropdown.
			 *
			 * @description This step guides the user to open the "Post type" dropdown.
			 * @next The next step is triggered automatically when the dropdown is opened.
			 * @listens 'uap/builder/legacy/field/select-opened'
			 */
			{
				id: 'trigger-open-select-post-type',
				title: this.strings.trigger_open_select_post_type.title,
				text: this.strings.trigger_open_select_post_type.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-triggers-ui .uap-item[data-integration="WP"][data-item="WP_USER_POST_PUBLISHED"] .form-element[data-field-code="WPPOSTTYPES"]',
					on: 'bottom'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour when the "Next" button is clicked.
					 *
					 * This method hides the WordPress label, resets the value of the title field, and types the text into the title field.
					 * After the text has been typed, it sets the recipe title and checks if the call was successful.
					 * If the call was successful, it gets the current step and enables the "Next" button.
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( event.detail.fieldCode !== 'WPPOSTTYPES' ) {
								return;
							}

							// Wait for the dropdown to open
							setTimeout( () => this.tour.next(), 200 );
						};

						this.addEventListener(
							'trigger-open-select-post-type',
							'uap/builder/legacy/field/select-opened',
							handler
						);
					}
				}
			},

			/**
			 * Step 9: Trigger select "Post" in "Post type" dropdown.
			 *
			 * @description This step guides the user to select the "Post" post type in the "Post type" dropdown.
			 * @next The next step is triggered automatically when the value of the dropdown changes
			 * @listens 'uap/builder/legacy/field/change'
			 * @listens 'uap/builder/legacy/field/select-closed'
			 */
			{
				id: 'trigger-change-select-post-type-value',
				title: this.strings.trigger_change_select_post_type_value.title,
				text: this.strings.trigger_change_select_post_type_value.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '.select2-results__options[id^="select2-WPPOSTTYPES"] .select2-results__options .select2-results__option:has(span[data-value="post"])',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 0,
				when: {
					/**
					 * Handles the change of the 'post' type selection and navigates the tour accordingly.
					 *
					 * This method sets up two event listeners:
					 * 1. 'uap/builder/legacy/field/change' - Proceeds to the next step of the tour when the selected value is 'post'.
					 * 2. 'uap/builder/legacy/field/select-closed' - Returns to the previous step of the tour when the selected value is not 'post'.
					 *
					 * @listens 'uap/builder/legacy/field/change'
					 * @listens 'uap/builder/legacy/field/select-closed'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( event.detail.value !== 'post' ) {
								return;
							}

							this.tour.next();
						};

						// eslint-disable-next-line require-jsdoc
						const maybeReturnToPreviousStep = event => {
							if ( event.detail.value === 'post' ) {
								return;
							}

							this.tour.back();
						};

						this.addEventListener(
							'trigger-change-select-post-type-value',
							'uap/builder/legacy/field/change',
							handler
						);

						this.addEventListener(
							'trigger-change-select-post-type-value',
							'uap/builder/legacy/field/select-closed',
							maybeReturnToPreviousStep
						);
					}
				}
			},

			/**
			 * Step 10: Save trigger
			 *
			 * @description This step instructs the user to save the configured trigger by clicking the "Save" button.
			 * @next The next step is triggered automatically when the save action is completed.
			 * @listens 'uap/builder/legacy/item-updated'
			 */
			{
				id: 'trigger-save-item',
				title: this.strings.trigger_save_item.title,
				text: this.strings.trigger_save_item.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-triggers-ui .item-options-button-save',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 10,
				when: {
					/**
					 * Proceeds to the next step of the tour when a specific trigger is updated.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/item-updated' event.
					 * When the event is triggered, it checks if the updated item is a 'trigger' with integration code 'WP' and item code 'WP_USER_POST_PUBLISHED'.
					 * If the condition is met, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/item-updated'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'trigger' ||
								event.detail.integrationCode !== 'WP' ||
								event.detail.itemCode !== 'WP_USER_POST_PUBLISHED'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'trigger-save-item',
							'uap/builder/legacy/item-updated',
							handler
						);
					}
				}
			},

			/**
			 * Step 11: Trigger status
			 *
			 * @description This step shows the user how to set the trigger status to "Live".
			 * @next The next step is triggered automatically when the trigger status is set to "Live".
			 * @listens 'uap/builder/legacy/item-changed-status'
			 */
			{
				id: 'trigger-status',
				title: this.strings.trigger_status.title,
				text: this.strings.trigger_status.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-triggers-ui .uap-item[data-integration="WP"][data-item="WP_USER_POST_PUBLISHED"] .uap-item-status',
					on: 'bottom'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour when a specific trigger's status is changed.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/item-changed-status' event.
					 * When the event is triggered, it checks if the updated item is a 'trigger' with integration code 'WP' and item code 'WP_USER_POST_PUBLISHED'.
					 * If the condition is met, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/item-changed-status'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'trigger' ||
								event.detail.integrationCode !== 'WP' ||
								event.detail.itemCode !== 'WP_USER_POST_PUBLISHED'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'trigger-status',
							'uap/builder/legacy/item-changed-status',
							handler
						);
					}
				}
			},

			/**
			 * Step 12: "Add action" button
			 *
			 * @description This step guides the user to add an action to the recipe.
			 * @next The next step is triggered automatically when the action button is clicked.
			 * @listens 'uap/builder/legacy/item-action-initial-clicked'
			 */
			{
				id: 'action-open-integrations',
				title: this.strings.action_open_integrations.title,
				text: `<p>${ this.strings.action_open_integrations.message1 }</p><p>${ this.strings.action_open_integrations.message2 }</p>`,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item-main-initial-action-button',
					on: 'right'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 10,
				when: {
					/**
					 * Proceeds to the next step of the tour when the 'action-open-integrations' event is triggered.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/item-action-initial-clicked' event.
					 * When the event is triggered, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/item-action-initial-clicked'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = () => this.tour.next();

						this.addEventListener(
							'action-open-integrations',
							'uap/builder/legacy/item-action-initial-clicked',
							handler
						);
					}
				}
			},

			/**
			 * Step 13: Action integrations
			 *
			 * @description This step shows the user the available integrations for actions.
			 * @next It contains a "Next" button that, when clicked, proceeds to the next step in the tour.
			 */
			{
				id: 'action-all-integrations',
				title: this.strings.action_all_integrations.title,
				text: this.strings.action_all_integrations.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#uap-action-all-integrations-link',
					on: 'right'
				},
				buttons: [
					{
						text: this.strings.action_all_integrations.next_btn_label,
						action: this.tour.next
					}
				]
			},

			/**
			 * Step 14: Select action integration
			 *
			 * @description This step guides the user to select the "Email" integration for the action.
			 * @next The next step is triggered automatically when the "Email" integration is selected.
			 * @listens 'uap/builder/legacy/integration-selected'
			 */
			{
				id: 'action-select-integration',
				title: this.strings.action_select_integration.title,
				text: this.strings.action_select_integration.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .item-integrations .item-integration[data-integration-id="EMAILS"]',
					on: 'right'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour when a specific action's integration is selected.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/integration-selected' event.
					 * When the event is triggered, it checks if the selected item is an 'action' with integration code 'EMAILS'.
					 * If the condition is met, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/integration-selected'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'action' ||
								event.detail.integrationCode !== 'EMAILS'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'action-select-integration',
							'uap/builder/legacy/integration-selected',
							handler
						);
					}
				}
			},

			/**
			 * Step 15: Select action
			 *
			 * @description This step guides the user to select the "Send an email" action.
			 * @next The next step is triggered automatically when the action is selected.
			 * @listens 'uap/builder/legacy/item-created'
			 */
			{
				id: 'action-select-action',
				title: this.strings.action_select_action.title,
				text: this.strings.action_select_action.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .item-task-selector__option[data-integration-id="EMAILS"][data-item-id="SENDEMAIL"]',
					on: 'top'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 0,
				when: {
					/**
					 * Proceeds to the next step of the tour when a specific action is created.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/item-created' event.
					 * When the event is triggered, it checks if the created item is an 'action' with integration code 'EMAILS' and item code 'SENDEMAIL'.
					 * If the condition is met, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/item-created'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'action' ||
								event.detail.integrationCode !== 'EMAILS' ||
								event.detail.itemCode !== 'SENDEMAIL'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'action-select-action',
							'uap/builder/legacy/item-created',
							handler
						);
					}
				}
			},

			/**
			 * Step 16: Email "To" field
			 *
			 * @description This step guides the user to fill in the "To" field for the email action.
			 * @next It contains a "Next" button that, when clicked, proceeds to the next step in the tour.
			 */
			{
				id: 'action-email-to-field',
				title: this.strings.action_email_to_field.title,
				text: this.strings.action_email_to_field.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILTO"]',
					on: 'bottom'
				},
				buttons: [
					{
						text: this.strings.action_email_to_field.next_btn_label,
						action: this.tour.next
					}
				]
			},

			/**
			 * Step 17: Email "To" token
			 *
			 * @description This step guides the user to use a token in the "To" field for dynamic email addressing.
			 * @next The next step is triggered automatically when the token selector is opened.
			 * @listens 'uap/builder/legacy/field/token-selector/open'
			 */
			{
				id: 'action-email-to-token',
				title: this.strings.action_email_to_token.title,
				text: `
					<p>${ this.strings.action_email_to_token.message1 }</p><p>${ this.strings.action_email_to_token.message2 }</p>
				`,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILTO"] .form-variable__toggle',
					on: 'left'
				},
				when: {
					/**
					 * Proceeds to the next step of the tour when the 'EMAILTO' field token selector is opened.
					 *
					 * This method sets up an event listener for the 'uap/builder/legacy/field/token-selector/open' event.
					 * When the event is triggered, it checks if the field code is 'EMAILTO'.
					 * If the condition is met, it proceeds to the next step of the tour.
					 *
					 * @listens 'uap/builder/legacy/field/token-selector/open'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( event.detail.fieldCode !== 'EMAILTO' ) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'action-email-to-token',
							'uap/builder/legacy/field/token-selector/open',
							handler
						);
					}
				}
			},

			/**
			 * Step 18: Email "Common" tokens group
			 *
			 * @description This step shows the user where to find the "Common" tokens group for the email recipient field.
			 * @next The next step is triggered automatically when the "Common" token group is opened.
			 * @listens 'uap/builder/legacy/field/token-selector/group/open'
			 * @listens 'uap/builder/legacy/field/token-selector/close'
			 */
			{
				id: 'action-email-to-token-group',
				title: this.strings.action_email_to_token_group.title,
				text: this.strings.action_email_to_token_group.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILTO"] .form-variable__dropdown .form-variable__group[data-id="COMMON"]',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 0,
				when: {
					/**
					 * Proceeds to the next step of the tour when the 'COMMON' token group is opened, and returns to the previous step when the token selector is closed.
					 *
					 * This method sets up two event listeners:
					 * 1. For the 'uap/builder/legacy/field/token-selector/group/open' event. When the event is triggered, it checks if the group code is 'COMMON'. If the condition is met, it proceeds to the next step of the tour.
					 * 2. For the 'uap/builder/legacy/field/token-selector/close' event. When the event is triggered, it returns to the previous step of the tour.
					 *
					 * @listens 'uap/builder/legacy/field/token-selector/group/open'
					 * @listens 'uap/builder/legacy/field/token-selector/close'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( event.detail.groupCode !== 'COMMON' ) {
								return;
							}

							this.tour.next();
						};

						// eslint-disable-next-line require-jsdoc
						const maybeReturnToPreviousStep = () => this.tour.back();

						this.addEventListener(
							'action-email-to-token-group',
							'uap/builder/legacy/field/token-selector/group/open',
							handler
						);

						this.addEventListener(
							'action-email-to-token-group',
							'uap/builder/legacy/field/token-selector/close',
							maybeReturnToPreviousStep
						);
					}
				}
			},

			/**
			 * Step 19: Select "Admin email" token
			 *
			 * @description This step guides the user to select the "Admin email" token for the email recipient field.
			 * @next The next step is triggered automatically when the "Admin email" token is selected.
			 * @listens 'uap/builder/legacy/field/token-selector/token/add'
			 * @listens 'uap/builder/legacy/field/token-selector/close'
			 */
			{
				id: 'action-email-to-token-select-token',
				title: this.strings.action_email_to_token_select_token.title,
				text: this.strings.action_email_to_token_select_token.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILTO"] .form-variable__dropdown .form-variable__group[data-id="COMMON"] .form-variable__group-item[data-token-id="admin_email"]',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 0,
				when: {
					/**
					 * Sets the value of the 'admin_email' token when it's selected and proceeds to the next step of the tour.
					 * Returns to the previous step when the token selector is closed and the 'admin_email' token was not selected.
					 *
					 * This method sets up two event listeners:
					 * 1. For the 'uap/builder/legacy/field/token-selector/token/add' event. When the event is triggered, it checks if the token code is 'admin_email'. If the condition is met, it sets the value of the field to '{{admin_email}}' and proceeds to the next step of the tour.
					 * 2. For the 'uap/builder/legacy/field/token-selector/close' event. When the event is triggered, it checks if the event was triggered by 'token-added'. If not, it returns to the previous step of the tour.
					 *
					 * @listens 'uap/builder/legacy/field/token-selector/token/add'
					 * @listens 'uap/builder/legacy/field/token-selector/close'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( event.detail.tokenCode !== 'admin_email' ) {
								return;
							}

							// Make sure only the token is selected
							event.detail.field.setValue( '{{admin_email}}' );

							this.tour.next();
						};

						// eslint-disable-next-line require-jsdoc
						const maybeReturnToPreviousStep = event => {
							// Only go back if the token was not selected
							if ( event.detail.triggeredBy === 'token-added' ) {
								return;
							}

							this.tour.show( 'action-email-to-token', false );
						};

						this.addEventListener(
							'action-email-to-token-select-token',
							'uap/builder/legacy/field/token-selector/token/add',
							handler
						);

						this.addEventListener(
							'action-email-to-token-select-token',
							'uap/builder/legacy/field/token-selector/close',
							maybeReturnToPreviousStep
						);
					}
				}
			},

			/**
			 * Step 20: Set email subject
			 *
			 * @description This step guides the user to set the subject of the email.
			 * @next It contains a "Next" button that, when clicked, proceeds to the next step in the tour.
			 */
			{
				id: 'action-email-set-subject',
				title: this.strings.action_email_set_subject.title,
				text: this.strings.action_email_set_subject.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILSUBJECT"]',
					on: 'bottom'
				},
				buttons: [
					{
						text: this.strings.action_email_set_subject.next_btn_label,
						action: this.tour.next
					}
				],
				when: {
					/**
					 * Sets the value of the email subject field in the recipe actions UI.
					 *
					 * This method first retrieves the subject field from the DOM. It then creates the subject string by formatting a predefined string with the "Post title" token. Finally, it sets the value of the subject field to the created string.
					 */
					show: () => {
						// Get the subject field
						// eslint-disable-next-line max-len
						const subjectField = document.querySelector( '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILSUBJECT"] input[name="EMAILSUBJECT"]' ).fieldInstance;

						// Create the subject
						const subject = _uo.utility.sprintf(
							this.strings.action_email_set_subject.subject_content,
							// Get the "Post title" token
							`{{${ this._triggerID }:WP_USER_POST_PUBLISHED:POSTTITLE}}`
						);

						// Set the subject
						subjectField.setValue( `${ subject }` );
					}
				}
			},

			/**
			 * Step 21: Set email body
			 *
			 * @description This step guides the user to set the body of the email.
			 * @next It contains a "Next" button that, when clicked, proceeds to the next step in the tour.
			 */
			{
				id: 'action-email-set-body',
				title: this.strings.action_email_set_body.title,
				text: this.strings.action_email_set_body.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILBODY"]',
					on: 'right'
				},
				buttons: [
					{
						text: this.strings.action_email_set_body.next_btn_label,
						action: this.tour.next
					}
				],
				when: {
					/**
					 * Sets the value of the email body field in the recipe actions UI.
					 *
					 * This method first retrieves the body field from the DOM. It then creates the body string by formatting a predefined string with the "Post title", "User email", and "Post URL" tokens. Finally, it sets the value of the body field to the created string and updates the TinyMCE tokens.
					 */
					show: () => {
						// Get the subject field
						// eslint-disable-next-line max-len
						const bodyField = document.querySelector( '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .form-element[data-field-code="EMAILBODY"] textarea[name="EMAILBODY"]' ).fieldInstance;

						// Create the body
						const body = _uo.utility.sprintf(
							this.strings.action_email_set_body.body_content,
							// Get the "Post title" token
							`{{${ this._triggerID }:WP_USER_POST_PUBLISHED:POSTTITLE}}`,
							// Get the "User email" token
							`{{user_email}}`,
							// Get the "Post URL" token
							`{{${ this._triggerID }:WP_USER_POST_PUBLISHED:POSTURL}}`
						);

						// Set the body
						bodyField.setValue( `${ body }` );

						// Update TinyMCE tokens
						bodyField.tokenSelector.parsePlainTokensInTinyMCE( {
							parseTokensInAttributes: false
						} );
					}
				}
			},

			/**
			 * Step 22: Save action
			 *
			 * @description This step instructs the user to save the configured action by clicking the "Save" button.
			 * @next The next step is triggered automatically when the save action is completed.
			 * @listens 'uap/builder/legacy/item-updated'
			 */
			{
				id: 'action-save-item',
				title: this.strings.action_save_item.title,
				text: this.strings.action_save_item.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .item-options-button-save',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 10,
				when: {
					/**
					 * Checks the details of the 'uap/builder/legacy/item-updated' event and proceeds to the next step if criteria are met.
					 *
					 * This method sets up a handler for the 'uap/builder/legacy/item-updated' event. The handler checks if the event details match specific criteria (itemType is 'action', integrationCode is 'EMAILS', and itemCode is 'SENDEMAIL'). If the criteria are met, the tour progresses to the next step. Otherwise, the function returns early.
					 *
					 * @listens 'uap/builder/legacy/item-updated'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'action' ||
								event.detail.integrationCode !== 'EMAILS' ||
								event.detail.itemCode !== 'SENDEMAIL'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'action-save-item',
							'uap/builder/legacy/item-updated',
							handler
						);
					}
				}
			},

			/**
			 * Step 23: Action status
			 *
			 * @description This step shows the user how to set the action status to "Live".
			 * @next The next step is triggered automatically when the action status is set to "Live".
			 * @listens 'uap/builder/legacy/item-changed-status'
			 */
			{
				id: 'action-status',
				title: this.strings.action_status.title,
				text: this.strings.action_status.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#recipe-actions-ui .uap-item[data-integration="EMAILS"][data-item="SENDEMAIL"] .uap-item-status',
					on: 'bottom'
				},
				// An amount of padding to add around the modal overlay opening
				modalOverlayOpeningPadding: 10,
				when: {
					/**
					 * Sets up an event listener for the 'uap/builder/legacy/item-changed-status' event and proceeds to the next step of the tour if criteria are met.
					 *
					 * This method sets up a handler for the 'uap/builder/legacy/item-changed-status' event. The handler checks if the event details match specific criteria (itemType is 'action', integrationCode is 'EMAILS', and itemCode is 'SENDEMAIL'). If the criteria are met, the tour progresses to the next step. Otherwise, the function returns early.
					 *
					 * @listens 'uap/builder/legacy/item-changed-status'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if (
								event.detail.itemType !== 'action' ||
								event.detail.integrationCode !== 'EMAILS' ||
								event.detail.itemCode !== 'SENDEMAIL'
							) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'action-status',
							'uap/builder/legacy/item-changed-status',
							handler
						);
					}
				}
			},

			/**
			 * Step 24: Recipe status
			 *
			 * @description This step guides the user to set the recipe status to "Live".
			 * @next The next step is triggered automatically when the recipe status is set to "Live".
			 * @listens 'uap/builder/legacy/recipe-changed-status'
			 */
			{
				id: 'recipe-status',
				title: this.strings.recipe_status.title,
				text: this.strings.recipe_status.message,
				attachTo: {
					// eslint-disable-next-line max-len
					element: '#uap-publish-metabox .publish-footer__right',
					on: 'top'
				},
				when: {
					/**
					 * Sets up an event listener for the 'uap/builder/legacy/recipe-changed-status' event and proceeds to the next step of the tour if the recipe is turned on.
					 *
					 * This method sets up a handler for the 'uap/builder/legacy/recipe-changed-status' event. The handler checks if the recipe is turned on (event.detail.isRecipeOn is true). If it is, the tour progresses to the next step. Otherwise, the function returns early.
					 *
					 * @listens 'uap/builder/legacy/recipe-changed-status'
					 */
					show: () => {
						// eslint-disable-next-line require-jsdoc
						const handler = event => {
							if ( ! event.detail.isRecipeOn ) {
								return;
							}

							this.tour.next();
						};

						this.addEventListener(
							'recipe-status',
							'uap/builder/legacy/recipe-changed-status',
							handler
						);
					}
				}
			},

			/**
			 * Step 25: Recipe created
			 *
			 * @description This step displays a message congratulating the user for successfully creating a recipe.
			 * @next The final button ends the tour.
			 */
			{
				id: 'recipe-created',
				title: this.strings.recipe_created.title,
				text: `<p>${ this.strings.recipe_created.message1 }</p><p>${ this.strings.recipe_created.message2 }</p>`,
				buttons: [
					{
						text: this.strings.recipe_created.next_btn_label,
						action: this.tour.complete
					}
				],
			},
		];
	}
}

new WalkthroughCreateRecipe();
